import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { realtime } from "./realtime";
import {
  applyRealtimeEvent,
  clearAccountQueries,
  evictClosedConversations,
  keys,
  protectionOptions,
  sessionId,
  snapshotOptions,
} from "./queries";
import { t } from "../i18n";
import { forgetLasting, notify, notifyUntilClosed } from "../ui/toast";

// The session's effects: the realtime socket, its bridge into the cache, and
// dropping what a past account or friendship may no longer read. Mount once;
// a second caller would connect and disconnect the socket too.
export function useSessionCore() {
  const client = useQueryClient();
  const snapshot = useQuery(snapshotOptions());
  // A call-link guest also signals over the socket; the rest needs an account.
  const userId = sessionId(snapshot.data),
    accountId = snapshot.data?.me?.id ?? null,
    guest = !!userId && !accountId;
  const [connectedUser, setConnectedUser] = useState<string | null>(null);

  const previousUser = useRef(accountId);
  useEffect(() => {
    if (previousUser.current !== accountId) {
      clearAccountQueries(client, previousUser.current);
      forgetLasting();
    }
    previousUser.current = accountId;
  }, [client, accountId]);
  useEffect(() => {
    if (snapshot.data?.authenticated)
      evictClosedConversations(client, snapshot.data);
  }, [client, snapshot.data]);
  useEffect(() => {
    if (!userId) return;
    const unsubscribe = realtime.subscribe((event) => {
      if (event.type === "connection") {
        setConnectedUser(event.connected ? userId : null);
        return;
      }
      applyRealtimeEvent(client, userId, event);
      // A guest's pass ending is expected; the call link page explains it.
      if (event.type === "session-ended")
        void client.invalidateQueries({ queryKey: keys.snapshot });
      if (event.type === "error") notify(event.message);
      if (event.type === "new-sign-in")
        notifyUntilClosed(
          t(
            "دخول جديد إلى حسابك: {where}. إذا لم يكن هذا أنت، فأخرجه من «الدخول والأجهزة» في الإعدادات.",
            {
              where: event.place
                ? `${event.device} · ${event.place}`
                : event.device,
            },
          ),
        );
      if (event.type === "sign-in-method-changed")
        notifyUntilClosed(
          event.method === "recovery"
            ? t(
                "أُنشئ رمز استعادة جديد لحسابك من جهاز آخر. إذا لم يكن هذا أنت، فافتح «الدخول والأجهزة» في الإعدادات وأخرج الأجهزة الأخرى، ثم أنشئ رمزًا جديدًا.",
              )
            : t(
                "أُضيف مفتاح مرور إلى حسابك من جهاز آخر. إذا لم يكن هذا أنت، فافتح «الدخول والأجهزة» في الإعدادات وأخرج الأجهزة الأخرى، ثم أزل مفتاح المرور هذا.",
              ),
        );
    });
    realtime.connect(userId);
    return () => {
      unsubscribe();
      realtime.disconnect();
    };
    // A guest who keeps in touch keeps their id, but the socket must reconnect
    // with the new account session.
  }, [client, userId, guest]);

  // A new guest must wait for their own socket, not the previous guest's state.
  return { snapshot, connected: !!userId && connectedUser === userId };
}

// The core keeps the session fresh; a screen opening does not refetch it.
export const useSnapshot = () =>
  useQuery({ ...snapshotOptions(), refetchOnMount: false });

export function useAccount() {
  const { data } = useSnapshot();
  return data?.authenticated ? data : null;
}

export function useGuest() {
  const { data } = useSnapshot();
  return data?.authenticated === false ? (data.guest ?? null) : null;
}

export function useProtection() {
  const query = useQuery(protectionOptions());
  return {
    protection: query.isFetching || query.isError ? undefined : query.data,
    failed: query.isError,
    retry: () => void query.refetch(),
  };
}
